/**
 * What is free here, stated as facts about this product only.
 *
 * Until 2026-10-21 this was a two-column table against "typical paid
 * builders", with a trial price, a four-weekly renewal range and an annual
 * total. Those figures could no longer be confirmed: published third-party
 * reports disagreed on the renewal price, and it varies by country. The other
 * column also made claims about every competitor at once ("their servers",
 * "a number, no reasons") that are true of some products and false of others.
 *
 * So the table now describes only what can be checked on this site, and the
 * note underneath makes the general point about paid builders without a
 * number in it. If competitor pricing comes back, it needs a source and a date
 * on the page, not a range from memory.
 */

import { Check } from 'lucide-react'

interface Row {
  claim: string
  value: string
}

const ROWS: Row[] = [
  { claim: 'Price', value: '$0. There is no paid tier, trial or subscription.' },
  { claim: 'Download a formatted PDF', value: 'Free and unlimited, with no watermark.' },
  { claim: 'Account', value: 'Not needed. There is no sign-up and no email capture.' },
  { claim: 'Where your resume is stored', value: 'In your own browser. There is no server copy.' },
  { claim: 'Text in the exported PDF', value: 'Real, selectable text, checked by the project’s automated tests.' },
  { claim: 'Working offline', value: 'The builder keeps working without a connection after your first visit.' },
  { claim: 'How findings are scored', value: 'Fixed rules: the same resume gets the same score, and every finding names its rule.' },
]

export const PricingComparison = ({ heading = 'What you get for free' }: { heading?: string }) => (
  <section aria-labelledby="pricing-comparison-heading" className="py-4">
    <h2 id="pricing-comparison-heading" className="text-2xl font-bold tracking-tight sm:text-3xl">
      {heading}
    </h2>
    <p className="mt-2 max-w-2xl text-balance text-base-content/75">
      Every tool and every export is free. Nothing on this site asks you to pay.
    </p>

    <div className="mt-5 overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        <caption className="sr-only">What ATSResumeKit includes at no cost</caption>
        <tbody>
          {ROWS.map((row) => (
            <tr key={row.claim} className="border-b border-base-300 align-top">
              <th scope="row" className="w-2/5 py-2.5 pr-3 text-left font-medium">
                {row.claim}
              </th>
              <td className="py-2.5">
                <span className="flex items-start gap-1.5">
                  <Check size={15} className="mt-0.5 shrink-0 text-success" aria-hidden="true" />
                  <span>{row.value}</span>
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>

    <p className="mt-3 max-w-3xl text-sm text-base-content/75">
      Many resume builders let you write for free and charge when you download, often through a
      short, low-cost trial that renews automatically. Prices and billing periods change and differ
      by country, so check the renewal terms before you pay for any resume tool.
    </p>
  </section>
)
